// Centralized API plumbing.
//
// Every backend fetch should use `API_BASE` so versioning changes land
// in one place. Every error-path read should use `apiErrorMessage` so
// the FE keeps working across a BE rollout (new `{error: {...}}` shape
// ↔ legacy `{msg: "..."}` shape).
//
// `authedFetch` adds the access token, and on a 401 transparently
// rotates the refresh token and retries the original request once.
//
// See entgo-blog-server/docs/design/api-v1-and-structured-errors.md
// and entgo-blog-server/docs/design/refresh-tokens-and-rate-limit.md.

export const API_BASE = '/v1';

// LocalStorage keys. Kept here so there's one place to rename if we
// ever switch to httpOnly cookies.
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// Pull a user-presentable message out of whatever error shape the BE
// happens to return. Preference order:
//   1. New structured envelope: {error: {code, message, field?}}.
//   2. Legacy flat: {msg: "..."}.
//   3. Sensible fallback.
// Callers that want to branch on the error code should read
// `res_json.error.code` directly.
export const apiErrorMessage = (res_json) => {
    if (res_json && res_json.error && res_json.error.message) {
        return res_json.error.message;
    }
    if (res_json && res_json.msg) {
        return res_json.msg;
    }
    return 'Something went wrong';
};

// Pull the machine-readable error code out of the envelope. Returns
// null when the response isn't an error or the BE didn't set one.
export const apiErrorCode = (res_json) =>
    (res_json && res_json.error && res_json.error.code) || null;

// --- Refresh-on-401 plumbing -------------------------------------------

// Coalesce concurrent refreshes. If N authed requests all 401 at the
// same time, only one should hit /auth/refresh; the others wait for
// its result. Without this, a burst of 401s would each rotate the
// refresh token, and all but the first would trip reuse-detection on
// the BE and log the user out.
let inFlightRefresh = null;

async function refreshAccessToken() {
    if (inFlightRefresh) return inFlightRefresh;

    inFlightRefresh = (async () => {
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
            clearTokensAndNotify();
            return null;
        }
        const form = new FormData();
        form.append('refresh_token', refreshToken);
        let res;
        try {
            res = await fetch(`${API_BASE}/auth/refresh`, {
                method: 'POST',
                body: form,
            });
        } catch (e) {
            // Network error — don't clear tokens; the user may just be
            // offline. Let the original caller see its 401.
            return null;
        }
        if (!res.ok) {
            // Refresh itself rejected — session is dead. Clear and
            // notify AuthState to move to logged-out.
            clearTokensAndNotify();
            return null;
        }
        const body = await res.json();
        localStorage.setItem(TOKEN_KEY, body.token);
        if (body.refresh_token) {
            localStorage.setItem(REFRESH_TOKEN_KEY, body.refresh_token);
        }
        return body.token;
    })();

    try {
        return await inFlightRefresh;
    } finally {
        inFlightRefresh = null;
    }
}

function clearTokensAndNotify() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // AuthState listens for this and dispatches LOGOUT so the UI
    // reacts. Using a window event keeps api.js free of any React /
    // context import.
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:expired'));
    }
}

// authedFetch is a drop-in replacement for `fetch(url, opts)` on any
// endpoint that requires an access token. It:
//   1. Injects the current token into the `x-auth-token` header
//      (overriding any caller-supplied value).
//   2. On a 401 response, calls /auth/refresh and retries the request
//      exactly once with the new token.
//   3. If refresh fails, returns the original 401 so the caller can
//      render its error state as it always has.
//
// The refresh + logout endpoints are NOT retried — they handle their
// own 401 semantics.
export async function authedFetch(url, options = {}) {
    const doFetch = (tok) => {
        const headers = { ...(options.headers || {}) };
        headers['x-auth-token'] = tok || '';
        return fetch(url, { ...options, headers });
    };

    const token = localStorage.getItem(TOKEN_KEY);
    let res = await doFetch(token);

    if (res.status !== 401) return res;
    // Don't enter an infinite loop: the refresh/logout/check-auth
    // endpoints own their own 401s.
    if (url.includes('/auth/refresh') || url.includes('/auth/logout') || url.endsWith('/auth')) {
        return res;
    }

    const newToken = await refreshAccessToken();
    if (!newToken) {
        // Session dead. Return the original 401 — caller's existing
        // 401 handling runs.
        return res;
    }
    return doFetch(newToken);
}
